import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ImportOrderProvider,
  useImportOrder,
} from "../providers/ImportOrderContext";
import { LayoutPartials, Common, ModalInform } from "@/components";
import UploadFile from "../components/UploadFile";
import MappingFields from "../components/MappingFields";
import UploadProgress from "../components/UploadProgress";
import ImportResult from "../components/ImportResult";
import ReviewImport from "../components/ReviewImport";
import FixRoutes from "../components/FixRoutes";
import FixAddresses from "../components/FixAddresses";
import { useAppSelector } from "@/sharedStore/hooks";
import { SUBSCRIPTION_PRODUCT_TYPE } from "@/configuration/globalVariable";
import { DocumentType } from "../models";

const ImportOrder = () => {
  const location = useLocation();
  const { state, dispatch, handleCancel } = useImportOrder();
  const navigate = useNavigate();
  const subscriptionProductType = useAppSelector(
    (state) => state.authReducer.user?.subscribedProductType
  );

  // Handle routing between steps
  useEffect(() => {
    const hash = location.hash;
    if (!hash) return;
    if (!state.selectedFile) {
      navigate(".", { replace: true });
    }
  }, [location, navigate, state.selectedFile]);

  useEffect(() => {
    const navigationSource = location.state?.navigationSource;
    if (navigationSource) {
      dispatch({
        type: "SET_NAVIGATION_SOURCE",
        payload: navigationSource.replace("#", ""),
      });
    }
  }, [location.state, dispatch]);

  const getPageTitle = () => {
    switch (location.hash) {
      case "#mapping-fields":
        return "Map your data";
      case "#upload-progress":
        return state.documentType === DocumentType.INVOICE
          ? "Upload Invoices"
          : "Upload Orders";
      case "#review":
        return "Review";
      case "#result":
        return state.importSuccess ? "Import Successful" : "Import Failed";
      case "#fix-addresses":
        return "Fix addresses";
      case "#fix-routes":
        return "Finalize Routes";
      default:
        return `Import New ${
          subscriptionProductType === SUBSCRIPTION_PRODUCT_TYPE.TOMDELIVERY
            ? "Invoices"
            : "Orders/Invoices"
        }`;
    }
  };

  const handleBack = () => {
    switch (location.hash) {
      case "#mapping-fields":
        navigate(".");
        break;
      case "#review":
        handleCancel({ mode: "retry", needConfirm: true });
        break;
      case "#fix-addresses":
        navigate("#review");
        break;
      case "#fix-routes":
        navigate("#review");
        break;
      default:
        handleCancel({ needConfirm: !!state.selectedFile });
        break;
    }
  };

  return (
    <LayoutPartials.BodyContent
      pageTitle={getPageTitle()}
      onTitleBack={
        location.hash !== "#result" && location.hash !== "#upload-progress"
          ? handleBack
          : undefined
      }
    >
      {!location.hash && <UploadFile />}
      {location.hash === "#mapping-fields" && <MappingFields />}
      {location.hash === "#upload-progress" && <UploadProgress />}
      {location.hash === "#review" && <ReviewImport />}
      {location.hash === "#result" && <ImportResult />}
      {location.hash === "#fix-addresses" && <FixAddresses />}
      {location.hash === "#fix-routes" && <FixRoutes />}

      {/* Back confirmation modal */}
      <ModalInform
        open={state.isOpenCancelInformModal}
        type="warning"
        title="Do you still want to cancel?"
        onClose={() =>
          dispatch({ type: "TOGGLE_CANCEL_MODAL", payload: false })
        }
        groupBtn={
          <div className="mt-8 flex justify-center">
            <Common.Button
              color="transparent"
              onClick={() =>
                dispatch({ type: "TOGGLE_CANCEL_MODAL", payload: false })
              }
            >
              No
            </Common.Button>
            <Common.Button className="ml-4" onClick={() => handleCancel({})}>
              Yes
            </Common.Button>
          </div>
        }
      />
    </LayoutPartials.BodyContent>
  );
};

const Index = () => {
  return (
    <ImportOrderProvider>
      <ImportOrder />
    </ImportOrderProvider>
  );
};

export default Index;
